import Phaser from 'phaser';
import { SoundManager } from '../systems/SoundManager';

export class MainMenuScene extends Phaser.Scene {
  private soundManager!: SoundManager;

  constructor() {
    super({ key: 'MainMenuScene' });
  }

  create(): void {
    this.soundManager = new SoundManager(this);

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Animated background
    const bg = this.add.rectangle(centerX, centerY, width, height, 0x1a1a1a);

    // Add animated grid pattern
    const graphics = this.add.graphics();
    graphics.lineStyle(1, 0x333333, 0.3);
    for (let x = 0; x < width; x += 40) {
      graphics.lineBetween(x, 0, x, height);
    }
    for (let y = 0; y < height; y += 40) {
      graphics.lineBetween(0, y, width, y);
    }

    // Decorative circles
    for (let i = 0; i < 15; i++) {
      const circle = this.add.circle(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        Phaser.Math.Between(20, 60),
        0x4CAF50,
        0.1
      );

      this.tweens.add({
        targets: circle,
        alpha: { from: 0.05, to: 0.15 },
        duration: Phaser.Math.Between(2000, 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }

    // Title with glow effect
    const titleGlow = this.add.text(centerX, centerY - 200, 'ROGUE CATS', {
      fontSize: '96px',
      color: '#66BB6A',
      fontStyle: 'bold'
    });
    titleGlow.setOrigin(0.5);
    titleGlow.setAlpha(0.3);
    titleGlow.setScale(1.1);

    const title = this.add.text(centerX, centerY - 200, 'ROGUE CATS', {
      fontSize: '96px',
      color: '#4CAF50',
      fontStyle: 'bold'
    });
    title.setOrigin(0.5);

    // Subtitle
    const subtitle = this.add.text(centerX, centerY - 130, '🐱 Survive the Mouse Invasion 🐭', {
      fontSize: '28px',
      color: '#CCCCCC',
      fontStyle: 'italic'
    });
    subtitle.setOrigin(0.5);

    // Animate title
    this.tweens.add({
      targets: [title, titleGlow],
      scale: { from: 0.8, to: 1 },
      alpha: { from: 0, to: 1 },
      duration: 800,
      ease: 'Back.out'
    });

    this.tweens.add({
      targets: subtitle,
      alpha: { from: 0, to: 1 },
      duration: 600,
      delay: 400
    });

    // Pulse animation for title
    this.tweens.add({
      targets: [title, titleGlow],
      scale: 1.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Instructions panel
    const panelY = centerY - 20;
    const panel = this.add.rectangle(centerX, panelY, 700, 280, 0x2a2a2a, 0.8);
    panel.setStrokeStyle(3, 0x4CAF50);
    panel.setAlpha(0);

    this.tweens.add({
      targets: panel,
      alpha: 1,
      duration: 500,
      delay: 600
    });

    // Instructions header
    const instructionsHeader = this.add.text(centerX, panelY - 110, '═══ HOW TO PLAY ═══', {
      fontSize: '24px',
      color: '#4CAF50',
      fontStyle: 'bold'
    });
    instructionsHeader.setOrigin(0.5);
    instructionsHeader.setAlpha(0);

    this.tweens.add({
      targets: instructionsHeader,
      alpha: 1,
      duration: 400,
      delay: 800
    });

    // Instructions list
    const instructions = [
      { icon: '⌨️', text: 'Move with WASD or Arrow Keys' },
      { icon: '🔫', text: 'Weapons auto-fire at nearby enemies' },
      { icon: '⭐', text: 'Collect XP to level up and choose upgrades' },
      { icon: '❤️', text: 'Grab health pickups to stay alive' },
      { icon: '🏆', text: 'Survive as long as you can!' }
    ];

    const startY = panelY - 65;
    const lineHeight = 38;

    instructions.forEach((instruction, index) => {
      const y = startY + (index * lineHeight);

      const icon = this.add.text(centerX - 310, y, instruction.icon, {
        fontSize: '24px'
      });
      icon.setOrigin(0, 0.5);
      icon.setAlpha(0);

      const text = this.add.text(centerX - 270, y, instruction.text, {
        fontSize: '20px',
        color: '#FFFFFF'
      });
      text.setOrigin(0, 0.5);
      text.setAlpha(0);

      this.tweens.add({
        targets: [icon, text],
        alpha: 1,
        x: '+=-10',
        duration: 400,
        delay: 900 + (index * 100),
        ease: 'Power2'
      });
    });

    // Start button
    const buttonY = centerY + 180;
    const startButton = this.add.rectangle(centerX, buttonY, 320, 70, 0x4CAF50);
    startButton.setInteractive({ useHandCursor: true });
    startButton.setStrokeStyle(4, 0x66BB6A);
    startButton.setAlpha(0);

    const startText = this.add.text(centerX, buttonY, '▶ START GAME', {
      fontSize: '32px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    startText.setOrigin(0.5);
    startText.setAlpha(0);

    // Animate button
    this.tweens.add({
      targets: [startButton, startText],
      alpha: 1,
      scale: { from: 0.8, to: 1 },
      duration: 500,
      delay: 1400,
      ease: 'Back.out'
    });

    // Button interactions
    startButton.on('pointerover', () => {
      startButton.setFillStyle(0x66BB6A);
      this.tweens.add({
        targets: [startButton, startText],
        scale: 1.08,
        duration: 200,
        ease: 'Power2'
      });
    });

    startButton.on('pointerout', () => {
      startButton.setFillStyle(0x4CAF50);
      this.tweens.add({
        targets: [startButton, startText],
        scale: 1,
        duration: 200,
        ease: 'Power2'
      });
    });

    startButton.on('pointerdown', () => {
      this.soundManager.playMenuClick();
      startButton.setFillStyle(0x388E3C);
      this.cameras.main.fadeOut(500);
      this.time.delayedCall(500, () => {
        this.scene.start('GameScene');
      });
    });

    // Keyboard input for start
    this.input.keyboard?.once('keydown-SPACE', () => {
      this.soundManager.playMenuClick();
      this.cameras.main.fadeOut(500);
      this.time.delayedCall(500, () => {
        this.scene.start('GameScene');
      });
    });

    this.input.keyboard?.once('keydown-ENTER', () => {
      this.soundManager.playMenuClick();
      this.cameras.main.fadeOut(500);
      this.time.delayedCall(500, () => {
        this.scene.start('GameScene');
      });
    });

    // Press any key hint
    const hint = this.add.text(centerX, buttonY + 60, 'Press SPACE or ENTER to start', {
      fontSize: '16px',
      color: '#888888',
      fontStyle: 'italic'
    });
    hint.setOrigin(0.5);
    hint.setAlpha(0);

    this.tweens.add({
      targets: hint,
      alpha: { from: 0, to: 0.8 },
      duration: 1000,
      delay: 2000,
      yoyo: true,
      repeat: -1
    });

    // Upgrades button
    const upgradesButtonY = buttonY + 130;
    const upgradesButton = this.add.rectangle(centerX, upgradesButtonY, 280, 60, 0x9C27B0);
    upgradesButton.setInteractive({ useHandCursor: true });
    upgradesButton.setStrokeStyle(4, 0xBA68C8);
    upgradesButton.setAlpha(0);

    const upgradesText = this.add.text(centerX, upgradesButtonY, '⭐ UPGRADES ⭐', {
      fontSize: '24px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    upgradesText.setOrigin(0.5);
    upgradesText.setAlpha(0);

    // Animate upgrades button
    this.tweens.add({
      targets: [upgradesButton, upgradesText],
      alpha: 1,
      scale: { from: 0.8, to: 1 },
      duration: 500,
      delay: 1700,
      ease: 'Back.out'
    });

    // Upgrades button interactions
    upgradesButton.on('pointerover', () => {
      upgradesButton.setFillStyle(0xBA68C8);
      this.tweens.add({
        targets: [upgradesButton, upgradesText],
        scale: 1.08,
        duration: 200,
        ease: 'Power2'
      });
    });

    upgradesButton.on('pointerout', () => {
      upgradesButton.setFillStyle(0x9C27B0);
      this.tweens.add({
        targets: [upgradesButton, upgradesText],
        scale: 1,
        duration: 200,
        ease: 'Power2'
      });
    });

    upgradesButton.on('pointerdown', () => {
      this.soundManager.playMenuClick();
      upgradesButton.setFillStyle(0x7B1FA2);
      this.cameras.main.fadeOut(500);
      this.time.delayedCall(500, () => {
        this.scene.start('MetaShopScene');
      });
    });

    // Fade in from black
    this.cameras.main.fadeIn(800);
  }
}
